import { CloseButton, Menu, Table, Text, Tooltip } from '@mantine/core';
import {
	IconChevronDown,
	IconChevronUp,
	IconCircleCheck,
	IconEdit,
} from '@tabler/icons-react';
import { useState } from 'react';
import { KeyedRow } from '../../../components/KeyedRow';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import {
	formatToCurrency,
	formatToDateString,
	formatToShortDateString,
	formatToTimeString,
} from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import { useBankingAuth } from '../hooks/useBankingAuth';
import type { BankingDto } from '../types/BankingDto';
import { ExtraBankingInfoRow } from './ExtraBankingInfoRow';
import { ProcessModal } from './ProcessModal';
import { VarianceModal } from './VarianceModal';

export default function BankingRow({
	initialBank,
}: { initialBank: BankingDto }) {
	const [banking, setBanking] = useState(initialBank);

	const { authMessage, processedMessage } = useBankingAuth(banking);

	const [isExtraInfoOpen, setIsExtraInfoOpen] = useState(false);

	const varianceModal = useModal();
	const processModal = useModal();

	return (
		<>
			<ProcessModal
				isOpened={processModal.isOpen}
				close={processModal.close}
				updateTableRow={setBanking}
				displayDate={`${formatToShortDateString(banking.dateTime)} ${formatToTimeString(banking.dateTime)}`}
				bankingId={banking.id}
			/>
			<VarianceModal
				isOpened={varianceModal.isOpen}
				close={varianceModal.close}
				updateTableRow={setBanking}
				initialBank={{
					id: banking.id,
					cash: banking.cash.toString(),
					cashInvoices: banking.cashInvoice.toString(),
					floatHead: banking.floatHeld.toString(),
					changeHeld: banking.changeHeld.toString(),
					varReason: banking.variance === 'None' ? '' : banking.variance,
				}}
			/>
			<KeyedRow>
				<Table.Td>{formatToDateString(banking.dateTime)}</Table.Td>
				<Table.Td>{formatToTimeString(banking.dateTime)}</Table.Td>
				<Table.Td>{undefinedCheck(banking.till)}</Table.Td>
				<Table.Td>{undefinedCheck(banking.branch)}</Table.Td>
				<Table.Td>{formatToCurrency(banking.grossSales)}</Table.Td>
				<Table.Td>{banking.variance}</Table.Td>
				<Table.Td>{banking.processed ? 'True' : 'False'}</Table.Td>
				<Table.Td>
					<Tooltip label={undefinedCheck(banking.enteredByEmail)}>
						<Text size="sm">{undefinedCheck(banking.enteredByName)}</Text>
					</Tooltip>
				</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!isExtraInfoOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setIsExtraInfoOpen(!isExtraInfoOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Tooltip label={authMessage} hidden={authMessage === undefined}>
							<Menu.Item
								leftSection={<IconEdit {...defaultIconStyle} />}
								onClick={() => varianceModal.open()}
								data-cy={'edit-button'}
								disabled={authMessage !== undefined}
							>
								Variance
							</Menu.Item>
						</Tooltip>

						<Tooltip
							label={authMessage ?? processedMessage}
							hidden={
								authMessage === undefined && processedMessage === undefined
							}
						>
							<Menu.Item
								leftSection={<IconCircleCheck {...defaultIconStyle} />}
								onClick={() => processModal.open()}
								data-cy={'edit-button'}
								disabled={
									authMessage !== undefined || processedMessage !== undefined
								}
							>
								Process
							</Menu.Item>
						</Tooltip>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={isExtraInfoOpen} columnRows={10}>
				<ExtraBankingInfoRow banking={banking} />
			</TableRowCollapsible>
		</>
	);
}
